import React from "react";

import { Modal, ModalBody, ModalHeader, ModalTitle } from "@common/components/modal";

import { trDts } from "@modules/datasheets/utils/i18n";
import { Flex } from "@common/components/Flex";
import Dropdown from "@common/containers/fields/Dropdown";
import { getTestAttributes } from "@common/utils/tests";
import { Button } from "@common/components/Button";

import {
  DatasheetPaperFormat,
  DatasheetPaperOrientation,
  DatasheetPrintingSettingsProps,
} from "./datasheet-printing-settings.types";
import { PAPER_FORMAT_CONFIG } from "./datasheet-printing-settings.constants";

import "./datasheet-printing-settings.styles.scss";

export const DatasheetPrintingSettings: React.FC<DatasheetPrintingSettingsProps> = (props) => {
  const { onClose, calculatePaperCount, onSubmit } = props;

  const paperFormats = React.useMemo(() => {
    return Object.entries(PAPER_FORMAT_CONFIG).map(([key, value]) => ({
      value: Number(key),
      label: value.isoName,
    }));
  }, []);

  const paperOrientation = React.useMemo(
    () =>
      Object.values(DatasheetPaperOrientation).map((orientation) => ({
        value: orientation,
        label: trDts(orientation),
      })),
    []
  );

  const [selectedPaperFormat, setSelectedPaperFormat] = React.useState<DatasheetPaperFormat>(DatasheetPaperFormat.A3);

  const [selectedPaperOrientation, setSelectedPaperOrientation] = React.useState<DatasheetPaperOrientation>(
    DatasheetPaperOrientation.portrait
  );

  const printButtonCount = React.useMemo(
    () => calculatePaperCount({ format: selectedPaperFormat, orientation: selectedPaperOrientation }),
    [calculatePaperCount, selectedPaperFormat, selectedPaperOrientation]
  );

  const printButtons = React.useMemo(
    () => Array.from({ length: printButtonCount }, (_, index) => `${trDts("Print Column")} ${index + 1}`),
    [printButtonCount]
  );

  return (
    <Modal
      show
      backdrop="static"
      bsClass="bootstrap modal"
      className="DatasheetsEditorDatasheetPrintingSettings"
      animation={false}
      onEscapeKeyDown={onClose}
      onHide={onClose}
    >
      <ModalHeader closeButton onHide={onClose}>
        <ModalTitle>{trDts("Datasheet Printing")}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <Flex width="100%">
          <div style={{ width: "200px" }}>
            <p {...getTestAttributes({ id: "lbl-paper-fmt-dialog-paper-format" })}>{trDts("Paper format")}</p>
            <Dropdown
              {...getTestAttributes({ id: "lbx-paper-fmt-dialog-paper-format" })}
              options={paperFormats}
              selectedValue={selectedPaperFormat}
              onValueChanged={setSelectedPaperFormat}
            />
          </div>
          <div style={{ width: "200px", marginLeft: "8px" }}>
            <p {...getTestAttributes({ id: "lbl-paper-fmt-dialog-paper-orin" })}>{trDts("Paper orientation")}</p>
            <Dropdown
              {...getTestAttributes({ id: "lbx-paper-fmt-dialog-paper-orin" })}
              options={paperOrientation}
              selectedValue={selectedPaperOrientation}
              onValueChanged={setSelectedPaperOrientation}
            />
          </div>
        </Flex>
        <Flex width="100%" className="DatasheetsEditorDatasheetPrintingSettings__print-actions">
          {printButtons.map((text, index) => (
            <Button
              {...getTestAttributes({ id: `btn-paper-fmt-dialog-print${index}` })}
              className="DatasheetsEditorDatasheetPrintingSettings__print-actions__btn"
              bsSize="small"
              bsStyle="primary"
              key={text}
              onClick={() =>
                onSubmit({ pagesToSkip: index, format: selectedPaperFormat, orientation: selectedPaperOrientation })
              }
            >
              {text}
            </Button>
          ))}
        </Flex>
      </ModalBody>
    </Modal>
  );
};
